import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import Preloader from 'ui-lib/components/Preloader';
import { useCancellableRequest } from 'web-shared/services/cancellable-request/hook';
import { parseUrlParams, stringifyUrlParams } from 'web-shared/utils/url-params';
import { SIGN_IN_ERROR } from 'web-shared/consts/action-types';
import { authValidate } from 'web-shared/actions/auth';
import { SSOService } from 'web-shared/services/sso-auth-service/SSOService';
import { UserImpersonation } from 'web-shared/services/user-impersonation';
import { SharedDispatch } from 'web-shared/reducers/types';
import { getRedirectPath } from 'web-shared/modules/auth/shared/utils';

import { parseLoginState } from './utils';

interface Props {
  ssoService: SSOService;
}

const SSOSignInCallbackPage = ({ ssoService }: Props) => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const request = useCancellableRequest();
  const dispatch = useDispatch<SharedDispatch>();

  useEffect(() => {
    const params = parseUrlParams(search);
    const parsedState = parseLoginState(params.state);
    const portalName = window.location.hostname.split('.')[0];

    const handleSignInError = () => {
      UserImpersonation.clearImpersonationData();
      dispatch({ type: SIGN_IN_ERROR });
      navigate('/sign-in', { replace: true });
    };

    const handleLogin = async (redirectData: { code: string }) => {
      try {
        const api = ssoService.getAuthTokenApiCall(redirectData.code);
        await request.send(api, undefined);
        await dispatch(authValidate());

        window.location.replace(getRedirectPath(parsedState?.from, { withPack: true }));
      } catch {
        handleSignInError();
      }
    };

    if (parsedState?.companySubdomain && portalName !== parsedState.companySubdomain) {
      const { state: _, ...restParams } = params;
      const querySearch = stringifyUrlParams({
        ...restParams,
        state: parsedState.from ? JSON.stringify({
          from: parsedState.from,
        }) : undefined,
      });

      window.location.replace(`https://${parsedState.companySubdomain}.over-haul.com/app/sign-in/${ssoService.getRouteUrl()}?${querySearch}`);
    } else if (params.code) {
      handleLogin({ code: params.code });
    } else {
      handleSignInError();
    }
  }, [search, dispatch, navigate, request, ssoService]);

  return (
    <Preloader />
  );
};

export default SSOSignInCallbackPage;
